export const BUZZ_SCORE_BASELINE = 60;
export const BUZZ_SCORE_BIN_SIZE = 10;
export const BUZZ_SCORE_BIN_COUNT = 10;

export const buildScoreBins = scores => {
  const bins = Array.from({ length: BUZZ_SCORE_BIN_COUNT }, (_, index) => {
    const min = index * BUZZ_SCORE_BIN_SIZE;
    const max = min + BUZZ_SCORE_BIN_SIZE;
    return {
      min,
      max,
      label: `${min}-${max}`,
      count: 0,
    };
  });

  scores.forEach(score => {
    if (score == null || Number.isNaN(Number(score))) return;
    const value = Number(score);
    const index = Math.min(
      Math.floor(value / BUZZ_SCORE_BIN_SIZE),
      BUZZ_SCORE_BIN_COUNT - 1
    );
    bins[index].count += 1;
  });

  return bins;
};

export const relativeBinLevels = bins => {
  const maxCount = Math.max(...bins.map(bin => bin.count), 1);
  return bins.map(bin => ({
    ...bin,
    relativeHeight: Math.round((bin.count / maxCount) * 100),
  }));
};

export const binDensityKey = bin => {
  if (bin.count === 0) return 'NONE';
  if (bin.relativeHeight >= 70) return 'MORE';
  if (bin.relativeHeight <= 30) return 'FEWER';
  return 'TYPICAL';
};

export const scoreBinIndex = score => {
  if (score == null || Number.isNaN(Number(score))) return null;
  const value = Number(score);
  return Math.min(
    Math.floor(value / BUZZ_SCORE_BIN_SIZE),
    BUZZ_SCORE_BIN_COUNT - 1
  );
};

export const scorePercentile = (score, peerScores) => {
  const peers = peerScores
    .map(value => Number(value))
    .filter(value => !Number.isNaN(value));
  if (!peers.length || score == null || Number.isNaN(Number(score))) {
    return null;
  }
  const value = Number(score);
  const below = peers.filter(peer => peer < value).length;
  return Math.round((below / peers.length) * 100);
};

export const scoreRank = (score, peerScores) => {
  const peers = peerScores
    .map(value => Number(value))
    .filter(value => !Number.isNaN(value))
    .sort((a, b) => b - a);
  if (!peers.length || score == null || Number.isNaN(Number(score))) {
    return null;
  }
  const value = Number(score);
  const rank = peers.findIndex(peer => peer <= value) + 1;
  return rank > 0 ? rank : peers.length;
};

export const clampScore = score => {
  if (score == null || Number.isNaN(Number(score))) return null;
  return Math.max(0, Math.min(100, Number(score)));
};

export const scoreColorClass = score => {
  const value = Number(score);
  if (Number.isNaN(value)) return 'text-n-slate-12';
  if (value >= 75) return 'text-n-teal-11';
  if (value >= 55) return 'text-n-brand';
  if (value >= 40) return 'text-n-amber-11';
  return 'text-n-ruby-11';
};

export const scoreAccentBarClass = score => {
  const value = Number(score);
  if (Number.isNaN(value)) return 'bg-n-slate-9';
  if (value >= 75) return 'bg-n-teal-9';
  if (value >= 55) return 'bg-n-brand';
  if (value >= 40) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
};

export const periodDeltaFromHistory = (history, score) => {
  const points = (history || [])
    .filter(point => point?.scored_at != null && point?.b != null)
    .map(point => ({
      at: new Date(point.scored_at).getTime(),
      b: Number(point.b),
    }))
    .sort((a, b) => a.at - b.at)
    .map(point => point.b);
  if (points.length < 2) return null;

  const current =
    score != null && score !== '' && !Number.isNaN(Number(score))
      ? Number(score)
      : points[points.length - 1];
  const previous = points[points.length - 2];
  const delta = current - previous;
  return delta === 0 ? null : delta;
};

export const resolvePeriodDelta = (periodDelta, history, score) => {
  if (periodDelta != null && periodDelta !== '') {
    const value = Number(periodDelta);
    if (!Number.isNaN(value) && value !== 0) return value;
  }
  return periodDeltaFromHistory(history, score);
};

export const formatDeltaLabel = delta => {
  if (delta == null || Number.isNaN(Number(delta))) return null;
  const value = Number(delta);
  if (value === 0) return null;
  return value > 0 ? `+${value}` : `${value}`;
};

export const deltaPillClass = delta => {
  const value = Number(delta);
  if (value > 0) {
    return 'rounded-full px-2 py-0.5 text-xs font-semibold bg-n-teal-3 text-n-teal-11';
  }
  return 'rounded-full px-2 py-0.5 text-xs font-semibold bg-n-ruby-3 text-n-ruby-11';
};
